<script setup lang="ts">
/**
 * Aura AGM (MULTIPLE shareholders) — Studio panel. Live document package + create task.
 * 형제 `agm`(Sole Member) 모듈의 cdd/서명/normalize 를 재사용. 차이: 멤버 전원 서명(members 루프),
 * 의장(chairman_name), 개인·법인 컨트롤러 Notice 분리.
 */
import { sgAppliedAddressLine } from '@/features/startTask/preset_task/sg/preset/utils/sgAppliedAddressLine';
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, bareKey, scopeOf, typeOf } from '../../agm/cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from '../../agm/cdd/agmSignersContext';
import AgmMultiPackageDoc from '../AgmMultiPackageDoc.vue';
import { normalizeAgmForWord } from '../utils/normalizeAgmForWord';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  appliedAddress?: {
    key: string; line?: string; line1?: string; line2?: string; line3?: string;
    town?: string; state?: string; postcode?: string; country?: string; seq?: number;
  } | null;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const fill = provideAgmFill();
const signersCtx = provideAgmSigners();
const docRef = ref<InstanceType<typeof AgmMultiPackageDoc> | null>(null);
const autofilledKeys = ref<Set<string>>(new Set());
const lastAutofillCd = ref('');
// Edit 재진입: 저장값 복원 후엔 회사 자동채움(비동기, 복원보다 늦게 끝남)이 직접 고친 값·부수(DDF/Notice)를 덮지 않게 한다.
//   빈 칸만 채우고 부수는 유지. 다른 회사로 바꾼 경우에만 새 회사 값으로 다시 채운다.
const restoredFromSaved = ref(false);
const autoLocal = ref<{ ddfIds: number[]; noticeIds: number[]; corpNoticeIds: number[] }>({ ddfIds: [1], noticeIds: [], corpNoticeIds: [] });
// 업무 설정의 processSteps (doc / t-sign / e-filing) — taskFlow 파생용.
const taskConfigSteps = ref<string[]>([]);

/** 주소 파트 → 한 줄. camelCase / UPPER_CASE 필드명 모두 지원. 해외주소 우선, 없으면 SG 주소. */
function buildCtrlAddress(dt: any): string {
  if (!dt || typeof dt !== 'object') return '';
  const g = (...keys: string[]) => { for (const k of keys) { const v = String((dt as any)[k] ?? '').trim(); if (v) return v; } return ''; };
  const fl1 = g('foreignAddressLine1', 'FOREIGN_ADDRESS_LINE_1');
  const fl2 = g('foreignAddressLine2', 'FOREIGN_ADDRESS_LINE_2');
  if (fl1 || fl2) return [fl1, fl2].filter(Boolean).join(', ');
  const block = g('blockNo', 'BLOCK_NO', 'block');
  const street = g('streetName', 'STREET_NAME', 'street');
  const building = g('buildingName', 'BUILDING_NAME', 'building');
  const level = g('addrLevel', 'ADDR_LEVEL', 'level');
  const unitNo = g('addrUnit', 'ADDR_UNIT', 'unit');
  const postal = g('postalCode', 'POSTAL_CODE');
  const unit = [level, unitNo].filter(Boolean).join('-');
  return [
    [block, street].filter(Boolean).join(' '),
    building, unit ? `#${unit}` : '', postal ? `Singapore ${postal}` : '',
  ].filter(Boolean).join(', ');
}
let signerSeq = 0;
function mkSigner(name: string, title: string, role = 'director'): SignerEntry {
  return { id: `agmm-sg-${signerSeq++}`, name: String(name || '').trim(), title, role, isManual: true, showTitle: true, _signerLayout: 'box' };
}
function isoDate(v: unknown): string {
  const s = String(v ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  return m ? `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}` : s;
}

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd || '').trim(),
);
provide(AGM_ENTITY_CD, entityCd);

const taskTitleLocal = ref('');
const titleModel = computed({ get: () => taskTitleLocal.value || props.taskTitle || '', set: (v: string) => { taskTitleLocal.value = v; } });
const effectiveTitle = computed(() => titleModel.value.trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  const pct = Math.min(100, Math.round((fill.filledCount.value / total) * 100));
  emit('progress-change', pct);
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

function lookupRoleFor(key: string): { role: string; scoped: boolean } {
  const b = bareKey(key);
  const sc = scopeOf(key);
  // secretary_name 은 회사 비서(공유키) — Notice 의 ctrl_/corpctrl_ 스코프 안에 있어도 SECRETARY 로 (스코프 체크보다 먼저).
  if (b === 'secretary_name') return { role: 'SECRETARY', scoped: true };
  if (/^(individual_|corporate_)?controller_/.test(b) || /^(ctrl_|corpctrl_)/.test(sc)) return { role: 'CONTROLLER', scoped: true };
  if (b === 'director_name' || b === 'chairman_name' || b === 'registered_holder' || /^ddf_/.test(sc)) return { role: 'DIRECTOR', scoped: true };
  if (b === 'member_name' || /_member_/.test(b)) return { role: 'SHAREHOLDER', scoped: true };
  return { role: 'CONTACT', scoped: false };
}
fill.setActivateHandler((key: string) => {
  const t = typeOf(key);
  if (t === 'entity') { emit('change-entity'); return; }
  if (t === 'person') {
    const { role, scoped } = lookupRoleFor(key);
    emit('request-person-lookup', { fieldGroup: 'aura-agm-multi', key: bareKey(key), scopedKey: key, defaultRole: role, lookupType: 'officer', entityCd: scoped ? entityCd.value : '' });
  }
});
fill.setAddressHandler((key: string) => { emit('request-address-input', key); });

// ── 자동채움 ─────────────────────────────────────────────
async function autofillFromEntity(entityCdRaw: string) {
  const cd = String(entityCdRaw || '').trim();
  if (!cd || cd === lastAutofillCd.value) return;
  const entityChanged = !!lastAutofillCd.value && lastAutofillCd.value !== cd;
  lastAutofillCd.value = cd;
  if (entityChanged) restoredFromSaved.value = false;
  for (const k of autofilledKeys.value) fill.setValue(k, '');
  if (entityChanged) {
    signersCtx.clearAll();
    for (const k of Object.keys(fill.values)) {
      if (/^(ddf_\d+\/|ctrl_\d+\/|corpctrl_\d+\/)/.test(k)) fill.setValue(k, '');
      // 이전 회사의 멤버 행·의장(직접 고친 값 포함)도 비운다 — "비어 있을 때만 채움" 규칙 때문에 남던 문제
      if (/^agm_member_/.test(k) || k === 'chairman_name' || k === 'member_name') fill.setValue(k, '');
    }
  }
  const written = new Set<string>();
  const set = (k: string, v: unknown) => {
    const s = String(v ?? '').trim();
    if (restoredFromSaved.value && String(fill.values[k] ?? '').trim()) return; // 저장값 보존
    if (s) { fill.setValue(k, s); written.add(k); }
  };

  let ddfCount = 1;
  let noticeIds: number[] = [];
  let corpNoticeIds: number[] = [];

  // 1) L_ENTITY 원본 (agm-source) — 회사·주소·FYE·회사유형·주주
  let agmSrcMembers: any[] = [];
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    set('company_name', src.companyName);
    set('company_registration_no', src.registrationNo);
    if (src.registeredOfficeAddress) set('registered_office_address', src.registeredOfficeAddress);
    set('financial_year_end', isoDate(src.financialYearEnd));
    set('financial_year_start_date', isoDate(src.financialYearStart));
    agmSrcMembers = Array.isArray(src.members) ? src.members : [];
    const ctCode = String(src.companyTypeCode || '').trim().toUpperCase();
    const A04_MAP: Record<string, string> = { A04001: 'epc_shares', A04002: 'private_shares', A04003: 'public_shares', A04004: 'public_guarantee' };
    if (A04_MAP[ctCode]) set('company_type', A04_MAP[ctCode]);
    set('company_activity', 'active');
  } catch (e) { console.warn('[AGM-multi] agm-source failed', e); }

  // 2) Directors — 이사 서명자(agm_Signers) + DDF 이사별 1부 + own-name 지분 자동
  const directorNames: string[] = [];
  try {
    const res: any = await entityMyApi.getEntityOfficer(cd, { positionGroup: 'director' });
    const off: any = res?.data?.data ?? res?.data ?? res ?? {};
    const officers: any[] = [...(off.autoOfficers || []), ...(off.manualOfficers || [])].filter((o) => String(o?.status || '') !== 'Cessation');
    for (const o of officers) {
      const nm = String(o.fullName || o.name || '').trim();
      if (nm && !directorNames.includes(nm)) directorNames.push(nm);
    }
  } catch (e) { console.warn('[AGM-multi] getEntityOfficer failed', e); }
  const norm = (s: string) => String(s || '').toUpperCase().replace(/\s+/g, ' ').trim();
  const fmtShares = (m: any): string => {
    const parts: string[] = [];
    if (Number(m?.sharesOrdinary) > 0) parts.push(`${Number(m.sharesOrdinary).toLocaleString()} Ordinary Shares`);
    if (Number(m?.sharesPreference) > 0) parts.push(`${Number(m.sharesPreference).toLocaleString()} Preference Shares`);
    if (Number(m?.sharesOthers) > 0) parts.push(`${Number(m.sharesOthers).toLocaleString()} Other Shares`);
    return parts.join(', ');
  };
  if (directorNames.length) {
    ddfCount = directorNames.length;
    set('agm_director_count', String(directorNames.length));
    set('director_name', directorNames[0]);
    // 의장 기본값 — 보통 첫 이사(운영자 변경 가능)
    if (!String(fill.values.chairman_name || '').trim()) set('chairman_name', directorNames[0]);
    const dirSigners = directorNames.map((nm) => mkSigner(nm, 'Director'));
    if (!signersCtx.signersOf('agm_Signers').length) signersCtx.setSigners('agm_Signers', [...dirSigners]);
    if (!signersCtx.signersOf('letter_Signers').length) signersCtx.setSigners('letter_Signers', [...dirSigners]);
    directorNames.forEach((nm, i) => {
      const sc = `ddf_${i + 1}/`;
      const sk = `${sc}ddf_Signers`;
      if (!signersCtx.signersOf(sk).length) signersCtx.setSigners(sk, [mkSigner(nm, 'Director')]);
      set(`${sc}director_name`, nm);
      const mem = agmSrcMembers.find((m) => m?.name && norm(m.name) === norm(nm));
      if (mem && Number(mem.sharesTotal) > 0) {
        set(`${sc}own_name_interests`, 'yes');
        set(`${sc}own_name_registered_holder`, nm);
        set(`${sc}own_name_interest_description`, fmtShares(mem));
        set(`${sc}own_name_amount_at_appointment`, Number(mem.sharesTotal).toLocaleString());
        set(`${sc}own_name_amount_at_end`, Number(mem.sharesTotal).toLocaleString());
      }
    });
  }

  // 3) Members — 주주 **전원** 을 멤버 필드(agm_member_{i}_*)로 시드(필드 방식, 문서 members 루프).
  //   멤버=사람(member_name), 법인 대표자(agm_member_{i}_representative_name)는 운영자가 입력.
  //   normalize 캡션 = "{대표자 법인} (Duly Appointed Representative of {멤버})".
  //   ※ 멤버↔대표법인 관계는 데이터에 없으므로 대표자 자동 시드는 하지 않는다(운영자 지정).
  try {
    // 현재 주주만 — 지분을 모두 넘겨 0주가 된 주주는 제외(고객 2026-11-04 Inspire Brands).
    //   주식 수가 전혀 없는 데이터(전원 0주)면 거를 근거가 없으므로 그대로 둔다.
    const withName = agmSrcMembers.filter((m) => m?.name);
    const held = withName.filter((m) => Number(m?.sharesTotal) > 0);
    const named = held.length ? held : withName;
    // 법인 멤버 판정 — isIndividual=false(B09001) 또는 회사 접미사(L.P./Pte/Ltd/LLP/…). 법인이면 "Has corporate representative" 자동 체크.
    const isCorpMember = (m: any) => m?.isIndividual === false || m?.isCorporate === true
      || /\b(l\.?p\.?|pte|ltd|limited|llp|inc|corp|holdings?|bhd|berhad|company|fund)\b/i.test(String(m?.name || ''));
    if (named.length) {
      set('member_name', String(named[0].name || '').trim());
      if (!String(fill.values.agm_member_count ?? '').trim()) {
        set('agm_member_count', String(named.length));
        named.forEach((m, i) => {
          set(`agm_member_${i + 1}_name`, String(m.name).trim());
          // 법인 멤버면 is_corporate=yes → 대표자 입력칸 노출(운영자가 대표자명 입력).
          if (isCorpMember(m)) set(`agm_member_${i + 1}_is_corporate`, 'yes');
        });
      }
    }
  } catch (e) { console.warn('[AGM-multi] members failed', e); }

  // 3b) Secretary — Notice 발송자(Company Secretary) 자동채움 (top-level secretary_name, 전 Notice 공유)
  try {
    const res: any = await entityMyApi.getEntityOfficer(cd, { positionGroup: 'secretary' });
    const off: any = res?.data?.data ?? res?.data ?? res ?? {};
    const secs: any[] = [...(off.autoOfficers || []), ...(off.manualOfficers || [])].filter((o) => String(o?.status || '') !== 'Cessation');
    const secName = String(secs[0]?.fullName || secs[0]?.name || '').trim();
    if (secName) set('secretary_name', secName);
  } catch (e) { console.warn('[AGM-multi] secretary failed', e); }

  // 4c) Auditor — AGM(Audited) 문서의 [[auditors_name]] 자동채움. 엔티티에 감사인(임원 positionGroup=auditor)이
  //     등록돼 있으면 그 이름(법인 감사인이면 법인명), 없으면 지금처럼 수기 입력.
  try {
    const res: any = await entityMyApi.getEntityOfficer(cd, { positionGroup: 'auditor' });
    const off: any = res?.data?.data ?? res?.data ?? res ?? {};
    const auds: any[] = [...(off.autoOfficers || []), ...(off.manualOfficers || [])].filter((o) => String(o?.status || '') !== 'Cessation');
    const a = auds[0] || {};
    const audName = String(a.fullName || a.name || a.entityName || a.companyName || a.contactName || '').trim();
    if (audName) set('auditors_name', audName);
  } catch (e) { console.warn('[AGM-multi] auditor failed', e); }

  // 4) Controllers — 개인/법인 분리. 개인 → ctrl_ Notice, 법인 → corpctrl_ Notice.
  try {
    const res: any = await fetch(`/api/entity/controller/${encodeURIComponent(cd)}`, { credentials: 'include' }).then((r) => r.json());
    const cands = [res?.data, res?.data?.data, res];
    let rawCtrls: any[] = [];
    for (const c of cands) { if (Array.isArray(c?.controllers)) { rawCtrls = c.controllers; break; } if (Array.isArray(c)) { rawCtrls = c; break; } }
    const seen = new Set<string>();
    const indiv: any[] = []; const corp: any[] = [];
    for (const c of rawCtrls) {
      if (/\d/.test(String(c?.dateOfCessation || ''))) continue;
      const nm = String(c?.name || c?.fullName || c?.FULL_NAME || '').trim();
      if (!nm || seen.has(nm.toLowerCase())) continue;
      seen.add(nm.toLowerCase());
      const cat = String(c?.categoryCode || c?.categoryCd || c?.CATEGORY_CD || '').toUpperCase();
      (cat === 'B09001' ? corp : indiv).push({ ...c, _name: nm });
    }
    async function ctrlDetail(c: any): Promise<any> {
      try {
        const ctrlIdx = c.ctrlIdx ?? c.CTRL_IDX;
        const rt = c.registerType || c.CTRL_REGISTER_TYPE || 'A06001';
        if (ctrlIdx == null || String(ctrlIdx) === '') return {};
        const dres: any = await fetch(`/api/entity/controller/${encodeURIComponent(cd)}/detail?ctrlIdx=${encodeURIComponent(String(ctrlIdx))}&registerType=${encodeURIComponent(String(rt))}`, { credentials: 'include' }).then((r) => r.json());
        return dres?.data ?? dres ?? {};
      } catch { return {}; }
    }
    // 개인 컨트롤러
    for (let i = 0; i < indiv.length; i++) {
      const c = indiv[i]; const sc = `ctrl_${i + 1}/`; const nm = c._name;
      set(sc + 'individual_controller_name', nm);
      set(sc + 'controller_full_name', nm);
      if (!signersCtx.signersOf(`${sc}notice_Signers`).length) signersCtx.setSigners(`${sc}notice_Signers`, [mkSigner(nm, 'Controller', 'contact')]);
      const dt = await ctrlDetail(c);
      set(sc + 'controller_alias', dt.aliases);
      set(sc + 'controller_email', dt.contactEmail);
      set(sc + 'controller_contact_no', [dt.areaCode, dt.mobileNo].map((s: any) => String(s || '').trim()).filter(Boolean).join(' '));
      set(sc + 'controller_nationality', dt.nationalityName);
      set(sc + 'controller_identification_no', dt.identificationNo);
      set(sc + 'individual_controller_identification_no', dt.identificationNo);
      set(sc + 'controller_date_of_birth', dt.dateOfBirth);
      set(sc + 'controller_date_of_becoming', dt.dateOfAppointed);
      set(sc + 'controller_date_of_cessation', dt.dateOfCessation);
      set(sc + 'controller_residential_address', buildCtrlAddress(dt));
    }
    // 법인 컨트롤러
    for (let i = 0; i < corp.length; i++) {
      const c = corp[i]; const sc = `corpctrl_${i + 1}/`; const nm = c._name;
      set(sc + 'corporate_controller_name', nm);
      set(sc + 'controller_entity_name', nm);
      if (!signersCtx.signersOf(`${sc}notice_Signers`).length) signersCtx.setSigners(`${sc}notice_Signers`, [mkSigner(nm, 'Corporate Controller', 'contact')]);
      const dt = await ctrlDetail(c);
      set(sc + 'controller_entity_uen', dt.identificationNo || dt.acrAuen || dt.nonAcrAuen);
      set(sc + 'controller_jurisdiction', dt.jurisdictionName || dt.jurisdiction);
      set(sc + 'controller_legal_form', dt.legalForm);
      set(sc + 'controller_corporate_register_name', dt.corporateEntityRegister);
      set(sc + 'controller_registered_office_address', buildCtrlAddress(dt));
      set(sc + 'controller_representative_name', dt.representativeName);
      set(sc + 'controller_representative_identification_no', dt.representativeIdentificationNo);
      set(sc + 'controller_entity_email', dt.contactEmail);
      set(sc + 'controller_entity_contact_no', [dt.areaCode, dt.mobileNo].map((s: any) => String(s || '').trim()).filter(Boolean).join(' '));
      set(sc + 'controller_date_of_becoming', dt.dateOfAppointed);
      set(sc + 'controller_date_of_cessation', dt.dateOfCessation);
    }
    noticeIds = indiv.map((_, i) => i + 1);
    corpNoticeIds = corp.map((_, i) => i + 1);
  } catch (e) { console.error('[AGM-multi] controllers failed', e); }

  if (!restoredFromSaved.value) autoLocal.value = {
    ddfIds: Array.from({ length: Math.max(1, ddfCount) }, (_, i) => i + 1),
    noticeIds, corpNoticeIds,
  };
  autofilledKeys.value = written;
  emitProgress();
}

watch(() => props.chatFormData, (fd) => {
  if (!fd) return;
  const nm = String(fd.company_name || fd.entityName || '').trim();
  if (nm && fill.values.company_name !== nm) fill.setValue('company_name', nm);
  const cd = String(fd.entityCd || fd.icpCd || '').trim();
  if (cd) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd) void autofillFromEntity(cd); }, { immediate: true });

function personField(p: any, ...keys: string[]): string {
  for (const k of keys) { const v = String(p?.[k] ?? '').trim(); if (v) return v; }
  return '';
}
function applyControllerPerson(scope: string, p: any) {
  const set = (k: string, v: string) => { if (String(v ?? '').trim()) fill.setValue(scope + k, String(v).trim()); };
  const name = personField(p, 'name', 'fullName', 'contactName', 'CONTACT_NAME');
  const corp = scope.startsWith('corpctrl_');
  if (corp) {
    set('corporate_controller_name', name);
    set('controller_entity_name', name);
    set('controller_entity_uen', personField(p, 'identificationNo', 'nricNo', 'contactRegNo'));
    set('controller_jurisdiction', personField(p, 'nationalityName', 'jurisdiction'));
    set('controller_registered_office_address', buildCtrlAddress(p) || personField(p, 'residentialAddress', 'address'));
    set('controller_entity_email', personField(p, 'email', 'contactEmail'));
    set('controller_entity_contact_no', personField(p, 'contactNo', 'mobileNo'));
    set('controller_date_of_becoming', personField(p, 'dateOfAppointed', 'dateOfBecoming'));
    return;
  }
  set('controller_full_name', name);
  set('individual_controller_name', name);
  set('controller_alias', personField(p, 'aliases', 'alias', 'aka'));
  set('controller_residential_address', buildCtrlAddress(p) || personField(p, 'residentialAddress', 'address', 'primaryAddress'));
  set('controller_email', personField(p, 'email', 'contactEmail', 'EMAIL'));
  set('controller_contact_no', personField(p, 'contactNo', 'mobileNo', 'MOBILE_NO'));
  set('controller_nationality', personField(p, 'nationalityName', 'nationality', 'NATIONALITY_NAME'));
  const idNo = personField(p, 'identificationNo', 'nricNo', 'idNo', 'contactRegNo', 'IDENTIFICATION_NO');
  set('controller_identification_no', idNo);
  set('individual_controller_identification_no', idNo);
  set('controller_date_of_birth', personField(p, 'dateOfBirth', 'dob'));
  set('controller_date_of_becoming', personField(p, 'dateOfAppointed', 'dateOfBecoming'));
}

watch(() => props.selectedPerson, (p) => {
  if (!p || !fill.activeKey.value) return;
  const key = fill.activeKey.value;
  const bare = bareKey(key);
  const sc = scopeOf(key);
  // 비서는 Notice 스코프 안에 있어도 컨트롤러로 채우면 안 됨 → 일반 적용(이름).
  if (bare === 'secretary_name') {
    fill.applyPersonToActive(p as Record<string, any>);
  } else if (/^(individual_|corporate_)?controller_/.test(bare) || /^(ctrl_|corpctrl_)/.test(sc)) {
    applyControllerPerson(sc, p as Record<string, any>);
  } else {
    fill.applyPersonToActive(p as Record<string, any>);
  }
  emitProgress();
});
watch(() => props.selectedAddress, (a) => {
  if (!a?.value || !fill.activeKey.value) return;
  fill.setValue(fill.activeKey.value, a.value);
  emitProgress();
});
watch(() => props.appliedAddress, (a) => {
  if (!a?.key) return;
  const line = sgAppliedAddressLine(a);
  if (line) { fill.setValue(a.key, line); emitProgress(); }
}, { deep: true });

onMounted(async () => {
  emitProgress();
  try {
    const cfg: any = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || '').trim());
    taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : [];
  } catch { /* ignore */ }
});

function gatherState() {
  return { values: { ...fill.exportPayload() }, local: docRef.value?.gatherLocalState?.() || null, signers: signersCtx.exportAll(), entityCd: entityCd.value, taskTitle: effectiveTitle.value };
}
function restoreState(s: any) {
  if (!s) return;
  restoredFromSaved.value = true;
  if (s.values && typeof s.values === 'object') { for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v)); }
  if (s.signers) signersCtx.importAll(s.signers);
  if (s.local) docRef.value?.restoreLocalState?.(s.local);
}

const TASK_CODE = 'CAU005';

function autoSigners(names: Array<{ name: unknown; title: string }>, role = 'director'): any[] | undefined {
  const out: any[] = [];
  names.forEach((n, i) => {
    const nm = String(n.name ?? '').trim();
    if (!nm) return;
    out.push({ id: `agmm-${role}-${i}`, name: nm, title: n.title, role, isManual: true, showTitle: true, _signerLayout: 'box' });
  });
  return out.length ? out : undefined;
}

type AgmKind = 'letter' | 'agm' | 'ddf' | 'notice' | 'corpnotice' | 'other';
function classifyAgmDoc(name: string): AgmKind {
  const n = String(name || '').toLowerCase().replace(/[_\-.]+/g, ' ');
  if (/\bddf\b|disclosure of interest|declaration form/.test(n)) return 'ddf';
  if (/(notice.*controller|change in particulars|386ah).*corporate|corporate.*(notice.*controller|change in particulars|386ah)/.test(n)) return 'corpnotice';
  if (/notice.*controller|change in particulars|386ah/.test(n)) return n.includes('corporate') ? 'corpnotice' : 'notice';
  if (/letter|notification|lodgement|annual return/.test(n)) return 'letter';
  if (/agm|resolutions in writing|annual general meeting|minutes|statement|solvency/.test(n)) return 'agm';
  return 'other';
}

const DDF_OWNED = [
  // 4개 이해관계 구분별 개별 키(own_name_* / family_* / deemed_* / corporate_*).
  ...['own_name', 'family', 'deemed', 'corporate'].flatMap((p) =>
    ['registered_holder', 'interest_description', 'amount_at_appointment', 'amount_at_end', 'date_price_details'].map((f) => `${p}_${f}`)),
  'corporation_name', 'interest_nature', 'director_name', 'resolution_date',
  'own_name_interests', 'family_interests', 'deemed_interests', 'corporate_interests', 'other_interests',
];
const NOTICE_OWNED = [
  'notice_date', 'individual_controller_name', 'individual_controller_identification_no',
  'is_still_controller', 'date_ceased_as_controller', 'has_change', 'date_of_change', 'particulars_of_change',
  'dated_on', 'controller_full_name', 'controller_alias', 'controller_residential_address', 'controller_email',
  'controller_contact_no', 'controller_nationality', 'controller_identification_no', 'controller_date_of_birth',
  'controller_date_of_becoming', 'controller_date_of_cessation',
];
const CORP_NOTICE_OWNED = [
  'notice_date', 'dated_on', 'is_still_controller', 'date_ceased_as_controller', 'has_change', 'date_of_change', 'particulars_of_change',
  'corporate_controller_name', 'controller_entity_name', 'controller_entity_uen', 'controller_jurisdiction', 'controller_legal_form',
  'controller_corporate_register_name', 'controller_registered_office_address', 'controller_representative_name',
  'controller_representative_identification_no', 'controller_entity_email', 'controller_entity_contact_no',
  'controller_date_of_becoming', 'controller_date_of_cessation',
];

function scopedFormData(scopePrefix: string, owned: string[]): Record<string, any> {
  const base = fill.exportPayload();
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(base)) {
    if (k.includes('/')) continue;
    if (owned.includes(bareKey(k))) continue;
    out[k] = v;
  }
  for (const [k, v] of Object.entries(base)) {
    if (!k.startsWith(scopePrefix)) continue;
    const bk = bareKey(k);
    const sv = String(v ?? '').trim();
    if (sv === '' && bk in out && String(out[bk] ?? '').trim() !== '') continue;
    out[bk] = v;
  }
  return normalizeAgmForWord(out);
}
function companyFormData(): Record<string, any> {
  const base = fill.exportPayload();
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(base)) { if (!k.includes('/')) out[k] = v; }
  return normalizeAgmForWord(out);
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = String(props.selection.task.TASK_CODE || TASK_CODE).trim() || TASK_CODE;
    const companyBase = companyFormData();
    const local: any = docRef.value?.gatherLocalState?.() || { ddfIds: [1], noticeIds: [], corpNoticeIds: [] };

    const agmSigners = signersCtx.signersOf('agm_Signers');
    const directorSigners = agmSigners.filter((s) => (s.role || '') === 'director' || /director/i.test(s.title || ''));
    companyBase.directors = directorSigners.map((s) => ({ director_name: s.name }));
    const dirNames: Array<{ name: unknown; title: string }> = directorSigners.map((s) => ({ name: s.name, title: 'Director' }));
    // 멤버 = 필드 방식(agm_member_{i}_*). companyFormData()→normalize 가 이미 members 배열
    //   (member_name + 법인 대표자 member_address)을 만들었으므로 여기서 덮어쓰지 않는다.

    const officerData = {
      entityCd: entityCd.value || null, icpCd: entityCd.value || null,
      presetForm: gatherState(), docType: 'SG_AGM_MULTI_AUDITED_PRE',
      isMyPreset: props.selection?.isMyPreset === true, formData: companyBase,
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskData: { formData: officerData } }) });
      await fetch(`/api/startTask/tasks/${taskId}/title`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskTitle: effectiveTitle.value }) });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null, taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'AGM (Audited, Multiple member)',
          taskTitle: effectiveTitle.value,
          taskFlow: (() => {
            const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
            return {
              createDocument: steps.length === 0 ? true : steps.includes('doc'),
              requestTSign: steps.includes('t-sign'),
              proceedWithEFiling: steps.includes('e-filing'),
            };
          })(),
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) { progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`; creating.value = false; return; }
    }

    progress.value = 'Preparing documents…';
    // Edit 재진입 시 selection.documents 가 원본 템플릿이 아니라 **생성된 부수 문서**(같은 tmpId 다수,
    //   이름에 "— Director 1" 등 접미사)일 수 있음 → tmpId 기준 dedupe 해야 인당 확장이 중복되지 않는다.
    const rawDocs = props.selection.documents || [];
    const seenTmpl = new Set<number>();
    const docs = rawDocs.filter((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return true;
      if (seenTmpl.has(tid)) return false;
      seenTmpl.add(tid);
      return true;
    });
    const companyLabel = String(companyBase.company_name || 'Company');
    // 생성 파일명에 이미 붙은 인당 접미사(pushOnce 가 " — <suffix>" em dash 로 추가)를 제거해
    //   재저장 시 접미사가 누적("— Director 1 — Director 1")되지 않게 한다. 원본 템플릿명은 " - "(hyphen)만 쓰므로 안전.
    const stripSuffix = (s: string) => s.split(' — ')[0].trim();
    type DocSave = { templateId: number; label: string; personName: string; formData: Record<string, any>; signers?: any[] };
    const docsToSave: DocSave[] = [];
    function pushOnce(d: any, formData: Record<string, any>, person: string, suffix = '', signers?: any[]) {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return;
      const baseLabel = stripSuffix(String(d?.templateName || d?.docCode || 'Document').replace(/\.docx?$/i, ''));
      docsToSave.push({ templateId: tid, label: suffix ? `${baseLabel} — ${suffix}` : baseLabel, personName: person, formData: normalizeAgmForWord(formData), signers: signers?.length ? signers : undefined });
    }
    const pick = (key: string, fallback?: any[]) => { const s = signersCtx.signersOf(key); return s.length ? s : (fallback && fallback.length ? fallback : undefined); };

    for (const d of docs) {
      const kind = classifyAgmDoc((d as any).templateName || (d as any).docCode || '');
      if (kind === 'ddf') {
        const ids: number[] = local.ddfIds?.length ? local.ddfIds : [1];
        for (const id of ids) {
          const fd = scopedFormData(`ddf_${id}/`, DDF_OWNED);
          const person = String(fd.director_name || `Director ${id}`);
          const signers = pick(`ddf_${id}/ddf_Signers`, autoSigners([{ name: fd.director_name, title: 'Director' }]));
          pushOnce(d, fd, person, dirNames.length > 1 ? `Director ${id}` : '', signers);
        }
        continue;
      }
      if (kind === 'notice') {
        const ids: number[] = local.noticeIds?.length ? local.noticeIds : [];
        for (const id of ids) {
          const fd = scopedFormData(`ctrl_${id}/`, NOTICE_OWNED);
          const person = String(fd.individual_controller_name || `Controller ${id}`);
          const signers = pick(`ctrl_${id}/notice_Signers`, autoSigners([{ name: fd.individual_controller_name, title: 'Controller' }], 'contact'));
          pushOnce(d, fd, person, ids.length > 1 ? `Controller ${id}` : '', signers);
        }
        continue;
      }
      if (kind === 'corpnotice') {
        const ids: number[] = local.corpNoticeIds?.length ? local.corpNoticeIds : [];
        for (const id of ids) {
          const fd = scopedFormData(`corpctrl_${id}/`, CORP_NOTICE_OWNED);
          const person = String(fd.corporate_controller_name || `Corporate Controller ${id}`);
          const signers = pick(`corpctrl_${id}/notice_Signers`, autoSigners([{ name: fd.corporate_controller_name, title: 'Corporate Controller' }], 'contact'));
          pushOnce(d, fd, person, ids.length > 1 ? `Corporate Controller ${id}` : '', signers);
        }
        continue;
      }
      // letter / agm — 회사 단위. [[Signers]] = 이사 서명(멤버는 members 루프로 별도 렌더).
      const signers = kind === 'letter' ? pick('letter_Signers', autoSigners(dirNames)) : pick('agm_Signers', autoSigners(dirNames));
      pushOnce(d, companyBase, companyLabel, '', signers);
    }

    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }) }).then((r) => r.json());
        if (!saveRes?.success) { progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`; }
      } catch (err: any) { progress.value = `⚠ Document save failed: ${err?.message || err}`; }
    }
    try { await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' } }); } catch { /* ignore */ }
    try { await fetch('/api/startTask/preset/use-log', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }) }); } catch { /* ignore */ }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
void scopeOf;
</script>

<template>
  <div class="agm-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'AGM (Audited, Multiple member)' }}</div>
        <div class="co-subtitle">Aura AGM (Audited · Multiple member) — live document package</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>

    <div v-if="!studio" class="co-title-row">
      <v-text-field v-model="titleModel" density="compact" variant="outlined" hide-details label="Task Title" />
    </div>

    <div class="co-doc">
      <AgmMultiPackageDoc ref="docRef" :auto-local="autoLocal" />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || creating" @click="createTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.agm-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.agm-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
